import { useMemo } from 'react';
import { MentorAvatarImg } from './MentorAvatarImg';
import type { RealMentor } from '../routes/Home';

// Slim auto-scrolling "ticker" of live mentors on the homepage — mirrors SuccessStoriesTicker
// structurally, but is prop-fed from Home's already-loaded `realMentors` (no extra network
// call) since every live mentor qualifies, unlike stories which need a written story and are
// often empty. Founders first (rewards early joiners, looks curated), capped at 20 so the
// loop stays snappy once the roster grows. Within each group the order is shuffled, not
// `mentors`' incoming (fixed, query-order) sequence — otherwise the same handful of mentors
// past position 20 would never appear in the ticker at all. Pauses on hover; reduced-motion
// users get a plain manual overflow-scroll instead of the auto-loop. Renders nothing if empty.
function initials(name: string): string {
  return name.split(/\s+/).map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
}

function shuffled<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j]!, copy[i]!];
  }
  return copy;
}

function subtitle(m: RealMentor): string {
  const list = m.examsTaught.length > 0 ? m.examsTaught
    : m.subjectsTaught.length > 0 ? m.subjectsTaught
      : m.sportsTaught.length > 0 ? m.sportsTaught
        : m.hobbies;
  return list.slice(0, 2).join(' · ');
}

function tagline(m: RealMentor): string {
  if (m.tagline) return m.tagline.length > 60 ? `${m.tagline.slice(0, 60)}…` : m.tagline;
  return m.yearsTeachingExperience > 0 ? `${m.yearsTeachingExperience}+ yrs experience` : '';
}

export function MentorsTicker({ mentors }: { mentors: RealMentor[] }) {
  const shown = useMemo(() => {
    const founders = shuffled(mentors.filter((m) => m.isFounder));
    const rest = shuffled(mentors.filter((m) => !m.isFounder));
    return [...founders, ...rest].slice(0, 20);
  }, [mentors]);

  // ~3s per card so a short list doesn't whip past; floor at 20s.
  const duration = Math.max(20, shown.length * 3);
  if (shown.length === 0) return null;
  const loop = [...shown, ...shown];

  return (
    <section style={{ padding: '1.1rem 1.25rem', background: 'white', border: '1px solid #ddd', borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: '1.5rem' }}>
      <style>{`
        .mnt { overflow: hidden; }
        .mnt-track { display: flex; gap: 0.75rem; width: max-content; }
        @media (prefers-reduced-motion: no-preference) {
          .mnt-track { animation: mnt-scroll ${duration}s linear infinite; }
          .mnt:hover .mnt-track { animation-play-state: paused; }
        }
        @media (prefers-reduced-motion: reduce) {
          .mnt { overflow-x: auto; scrollbar-width: none; }
          .mnt::-webkit-scrollbar { display: none; }
        }
        @keyframes mnt-scroll { from { transform: translateX(0); } to { transform: translateX(-50%); } }
      `}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <h2 style={{ margin: 0, color: '#1a4480', fontSize: '1.1rem' }}>🧑‍🏫 Our Mentors</h2>
        <a href="#mentors" style={{ color: '#1a4480', fontWeight: 600, fontSize: '0.85rem', textDecoration: 'none' }}>See all →</a>
      </div>
      <div className="mnt">
        <div className="mnt-track">
          {loop.map((m, i) => (
            <a key={`${m.mentorId}-${i}`} href={`/m/${m.slug}`} aria-hidden={i >= shown.length ? true : undefined}
              style={{ flex: '0 0 auto', width: 'min(300px, 82vw)', display: 'flex', gap: '0.6rem', alignItems: 'center', padding: '0.6rem 0.75rem', border: '1px solid #eef2f7', borderRadius: 10, background: '#fafbfc', textDecoration: 'none', color: 'inherit' }}>
              <div style={{ flexShrink: 0 }}>
                {m.profilePhotoUrl
                  ? <MentorAvatarImg src={m.profilePhotoUrl} alt={m.displayName} px={40} style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover', background: '#eee' }} />
                  : <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#1a4480', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.85rem' }}>{initials(m.displayName)}</div>}
              </div>
              {m.demoVideoId && (
                <span style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.15rem', padding: '0.25rem 0.35rem', borderRadius: 6, background: '#eef2ff', border: '1px solid #c7d2fe', color: '#1a4480' }}>
                  <span style={{ width: 16, height: 16, borderRadius: '50%', background: '#1a4480', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.55rem' }}>▶</span>
                  <span style={{ fontSize: '0.52rem', fontWeight: 700, lineHeight: 1.05, whiteSpace: 'nowrap' }}>Demo Video</span>
                </span>
              )}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#15294d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.displayName}</div>
                <div style={{ fontSize: '0.75rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subtitle(m)}</div>
                <div style={{ fontSize: '0.72rem', color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tagline(m)}</div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
